import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICmdbSamSWInstall } from '../../../services/serviceNow/cmdbSamSWInstall/cmdbSamSWInstall.model';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import {
  deleteCmdbSamSWInstall,
  getCmdbSamSWInstallById,
  searchCmdbSamSWInstall,
  saveCmdbSamSWInstall,
  processDataCmdbSamSWInstall,
} from './cmdbSamSWInstall.action';
import { ICmdbSamSWInstallState } from './cmdbSamSWInstall.model';

export const initialState: ICmdbSamSWInstallState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  processData: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const CmdbSamSWInstallSlice = createSlice({
  name: 'CmdbSamSWInstall',
  initialState,
  reducers: {
    clearCmdbSamSWInstall: () => {
      return initialState;
    },
    clearCmdbSamSWInstallMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
      state.processData.messages = [];
    },
    clearCmdbSamSWInstallGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCmdbSamSWInstall.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCmdbSamSWInstall.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ICmdbSamSWInstall>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchCmdbSamSWInstall.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCmdbSamSWInstallById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCmdbSamSWInstallById.fulfilled.type]: (state, action: PayloadAction<ICmdbSamSWInstall>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCmdbSamSWInstallById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCmdbSamSWInstall.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCmdbSamSWInstall.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCmdbSamSWInstall.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCmdbSamSWInstall.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCmdbSamSWInstall.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCmdbSamSWInstall.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },

    // Process Data
    [processDataCmdbSamSWInstall.pending.type]: (state) => {
      state.processData.loading = true;
      state.processData.messages = [];
    },
    [processDataCmdbSamSWInstall.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.processData.loading = false;
      state.processData.hasErrors = false;
      state.processData.messages = action.payload.messages;
    },
    [processDataCmdbSamSWInstall.rejected.type]: (state) => {
      state.processData.loading = false;
      state.processData.hasErrors = true;
    },
  },
});

// A selector
export const CmdbSamSWInstallSelector = (state: RootState) => state.cmdbSamSWInstall;

// Actions
export const {
  clearCmdbSamSWInstall,
  clearCmdbSamSWInstallMessages,
  clearCmdbSamSWInstallGetById,
  setTableColumnSelection,
} = CmdbSamSWInstallSlice.actions;

// The reducer
export default CmdbSamSWInstallSlice.reducer;
